import { buildPageContext, buildPageIdentity } from "./buildPageContext";

/** Maximum length of any single context value before it is truncated. */
const MAX_VALUE_CHARS = 128;

/** Page snapshots carry far more signal than the identifier fields, so they get a larger budget. */
const MAX_PAGE_SNAPSHOT_CHARS = 5000;

/**
 * Context keys emitted in a fixed order so the agent receives identifiers in a predictable
 * sequence regardless of the order the host page published them.
 */
const ORDERED_CONTEXT_KEYS: ReadonlyArray<string> = [
  "app",
  "route",
  "page_snapshot",
  "claim_id",
  "member_id",
  "client_id",
  "external_member_id",
  "person_code",
];

/** Page-specific values a host page can contribute to the agent's context. */
export type AdditionalPageContext = Record<string, unknown>;

/** Options controlling how much of the host page is captured. */
export interface BuildAdditionalContextOptions {
  /**
   * When `true`, a text snapshot of the rendered page is included. This scrapes visible DOM text,
   * which on most screens contains member and claim data, so it is off by default and must be
   * opted into by pages that need it.
   */
  readonly isPageSnapshotEnabled?: boolean;
}

function limitForKey(key: string): number {
  return key === "page_snapshot" ? MAX_PAGE_SNAPSHOT_CHARS : MAX_VALUE_CHARS;
}

function normalizeValue(key: string, value: unknown): string | null {
  if (value === null || value === undefined) return null;

  const text = typeof value === "string" ? value : String(value);
  const trimmed = text.trim();
  if (!trimmed) return null;

  return trimmed.slice(0, limitForKey(key));
}

/**
 * Merges the current page context with any page-specific values and normalizes the result:
 * values are stringified, trimmed, length-capped, and empty entries are dropped. Known keys are
 * emitted first in a stable order, followed by any remaining custom keys.
 *
 * The shell stays authoritative for `app`, `route`, and `page_snapshot`: a page cannot overwrite
 * where the agent is told the user actually is.
 */
export function buildAdditionalContext(
  additionalContext?: AdditionalPageContext,
  options?: BuildAdditionalContextOptions,
): Record<string, string> {
  const pageContext = options?.isPageSnapshotEnabled ? buildPageContext() : buildPageIdentity();

  const merged: AdditionalPageContext = {
    ...(additionalContext ?? {}),
    ...pageContext,
  };

  /* the snapshot scrapes member and claim data off the screen, so a page cannot smuggle one in by
     publishing the key itself when the opt-in is off. */
  if (!options?.isPageSnapshotEnabled) {
    delete merged["page_snapshot"];
  }

  const customKeys = Object.keys(merged).filter((key) => !ORDERED_CONTEXT_KEYS.includes(key));
  const orderedKeys = [...ORDERED_CONTEXT_KEYS, ...customKeys];

  const context: Record<string, string> = {};
  for (const key of orderedKeys) {
    const value = normalizeValue(key, merged[key]);
    if (value !== null) {
      context[key] = value;
    }
  }

  return context;
}
